import { ReactNode, useEffect, useRef, useState } from "react";

import { ImageCropModal, type CropMode, type ImageCropResult } from "../../components/ImageCropModal";
import type { CropProps } from "../../components/UploadImageInput";
import constants from "../../constants";
import { api } from "../../services/api";
import { compressImage } from "../../utils/compressImage";
import { useCropImage, type CropOptions } from "../CropImage";

type CropState = {
  file: File;
  aspect: number;
  cropShape: "round" | "rect";
  cropMode?: CropMode;
  resolve: (result?: ImageCropResult) => void;
};

type Options = {
  siteId: string;
  /** Asks to crop it first (a GIF goes whole). */
  crop?: CropProps;
  onUpload: (url: string, cropResult?: ImageCropResult) => void;
  /**
   * The caller shows the upload in place (the screen stays usable): told when the file starts going up,
   * after the crop, and when it fails (without it, a failure is thrown).
   */
  onUploadStart?: () => void;
  onUploadError?: (error: unknown) => void;
};

/**
 * Sends an image to the bucket: cropped first when asked (`cropModal` must be rendered), then lighter. Used by the
 * file input and by anything else that has a file (a dropped one, one downloaded from Pexels).
 */
export const useImageUpload = ({ siteId, crop, onUpload, onUploadStart, onUploadError }: Options) => {
  const { crop: cropImage } = useCropImage();
  const [isUploading, setUploading] = useState(false);
  const [cropState, setCropState] = useState<CropState | undefined>();
  // The screen stays usable while the file goes up: the end of the upload calls the callbacks of the
  // latest render (with what was typed meanwhile), and nothing at all once this is off the screen
  const callbacks = useRef({ onUpload, onUploadStart, onUploadError });
  callbacks.current = { onUpload, onUploadStart, onUploadError };
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const upload = async (file: File) => {
    const shouldCrop = file.type !== "image/gif" && !!crop;

    try {
      const cropResult = await (shouldCrop
        ? new Promise<ImageCropResult | undefined>(resolve => {
            setCropState({
              file,
              resolve,
              aspect: crop!.slice.aspect,
              cropShape: crop!.cropShape,
              cropMode: crop!.cropMode,
            });
          })
        : Promise.resolve<ImageCropResult | undefined>(undefined));

      if (shouldCrop && !cropResult) {
        return;
      }

      setUploading(true);
      callbacks.current.onUploadStart?.();

      let fileToUpload = file;

      if (cropResult?.area && shouldCrop) {
        const cropOptions: CropOptions = {
          file,
          ...cropResult.area,
          filename: file?.name,
        };
        if (crop?.cropMode !== "free" && crop?.size?.width) {
          cropOptions.targetWidth = crop.size.width;
        }
        if (crop?.cropMode !== "free" && crop?.size?.height) {
          cropOptions.targetHeight = crop.size.height;
        }

        fileToUpload = await cropImage(cropOptions);
      }

      // Cropped or whole, the image goes lighter (smaller side, JPEG / WebP): its type may change
      fileToUpload = await compressImage(fileToUpload);

      const authorization = await api.authorizeUpload(siteId, fileToUpload.type);
      await api.upload(authorization, fileToUpload, fileToUpload.type);
      if (isMounted.current) {
        callbacks.current.onUpload(`${constants.staticBucket}/${authorization.id}`, cropResult);
      }
    } catch (error) {
      if (!isMounted.current) {
        return;
      }
      if (!callbacks.current.onUploadError) {
        throw error;
      }
      callbacks.current.onUploadError(error);
    } finally {
      if (isMounted.current) {
        setUploading(false);
      }
    }
  };

  const cropModal: ReactNode = cropState && (
    // @ts-ignore
    <ImageCropModal
      file={cropState.file}
      onSave={(result?: ImageCropResult) => {
        cropState.resolve(result);
        setCropState(undefined);
      }}
      aspect={cropState.aspect}
      cropShape={cropState.cropShape}
      cropMode={cropState.cropMode}
    />
  );

  return { upload, isUploading, cropModal };
};
